import { z } from 'zod';
import {
  DEFAULT_CONFIG,
  KALSHI_TAKER_FEES,
  calculateAllocation,
  heldTradePnl,
  tradeFees,
  validContractPrice,
  type FeeModel,
} from '@whaletail/signal-engine';
import type { Btc15PredictorResponse } from './btc15Types';
import {
  applyOfficialResultsTo,
  incomingSupersedes,
  proxyActual,
  type OfficialRoundResult,
  type SettlementSource,
} from './btc15OfficialSettlement';
import type { ModelPrediction } from './modelPredictions';

export const MODEL_HISTORY_SCHEMA_VERSION = 1;
const MAX_HISTORY = 1200;

/**
 * A leaner sibling of `Btc15PredictionRecord` (btc15History.ts), for V2/V3
 * rather than V1. Deliberately excludes V1's descriptive scorecard fields
 * (regime/trendKey/distanceBucket/scoreBucket/rawScore) — V2/V3 don't
 * compute a "similar setup" bucket the way V1's calibrate() does, so those
 * fields would only ever hold meaningless placeholders here. There's also
 * no `recommendation`/SKIP concept: every round with a fitted model and a
 * live quote is recorded, and `calculateAllocation`'s own minimum-edge gate
 * (see computeModelStrategyPnl) is what naturally leaves a round unfunded,
 * exactly as visibly as V1's SKIP does via `valueSkips`.
 */
export interface ModelPredictionRecord {
  roundId: string;
  createdMs: number;
  roundCloseMs: number;
  roundOpenPrice: number;
  action: 'UP' | 'DOWN';
  probability: number;
  contractEntryPrice: number | null;
  contractQuoteSource: 'live_ask' | 'unavailable';
  settled: boolean;
  settledMs: number | null;
  closePrice: number | null;
  actual: 'UP' | 'DOWN' | null;
  won: boolean | null;
  /** Kalshi's target line (`floor_strike`) for this round, when known. */
  targetLine?: number | null;
  /** Where `actual`/`won` came from. Absent on older records, which were proxy-settled. */
  settlementSource?: SettlementSource;
}

export interface ModelHistory {
  schemaVersion: number;
  predictions: ModelPredictionRecord[];
}

export function emptyModelHistory(): ModelHistory {
  return { schemaVersion: MODEL_HISTORY_SCHEMA_VERSION, predictions: [] };
}

function isStorageAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

/** Sorts oldest-first and caps to `MAX_HISTORY`, stamping the current schema version. */
export function trimModelHistory(history: ModelHistory): ModelHistory {
  return {
    schemaVersion: MODEL_HISTORY_SCHEMA_VERSION,
    predictions: [...history.predictions]
      .sort((a, b) => a.createdMs - b.createdMs)
      .slice(-MAX_HISTORY),
  };
}

export function loadModelHistory(storageKey: string): ModelHistory {
  if (!isStorageAvailable()) return emptyModelHistory();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return emptyModelHistory();
    const parsed = parseModelHistory(raw);
    return parsed.ok && parsed.history ? parsed.history : emptyModelHistory();
  } catch {
    return emptyModelHistory();
  }
}

export function saveModelHistory(storageKey: string, history: ModelHistory): void {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(trimModelHistory(history)));
  } catch {
    // Storage may be unavailable (private browsing quota); fail silently,
    // matching the rest of the app's localStorage conventions.
  }
}

/**
 * `response.quote.contractPrice` is priced for `response.quote.action` —
 * V1's own live call, which this round's V2/V3 `action` frequently
 * disagrees with. Buying the YES contract and buying the NO contract are
 * different instruments at different prices, so sizing a DOWN prediction
 * off a quote priced for UP (or vice versa) would size against a price
 * nobody could actually have bought that contract at. `yesAsk`/`noAsk` are
 * both already on the quote regardless of which side V1 called, so this
 * picks the one that actually matches this model's own predicted action.
 */
function priceForAction(
  quote: Btc15PredictorResponse['quote'],
  action: ModelPrediction['action'],
): number | null {
  if (quote.source !== 'live_ask') return null;
  const price = action === 'UP' ? quote.yesAsk : quote.noAsk;
  return validContractPrice(price ?? 0) ? price : null;
}

/**
 * Records one observation per round (same "one per round, attach a late
 * quote" rule as V1's recordObservation) — but only when `prediction` is
 * non-null, i.e. a fitted model actually existed for this round. A round
 * with no model yet simply leaves no record, rather than a fabricated one.
 */
export function recordModelObservation(
  history: ModelHistory,
  response: Btc15PredictorResponse,
  prediction: ModelPrediction | null,
): ModelHistory {
  if (!prediction) return history;

  const existingIndex = history.predictions.findIndex(
    (p) => p.roundId === response.round.id,
  );
  const quotePrice = priceForAction(response.quote, prediction.action);

  if (existingIndex === -1) {
    const record: ModelPredictionRecord = {
      roundId: response.round.id,
      createdMs: Date.parse(response.generatedAt),
      roundCloseMs: response.signal.roundCloseMs,
      roundOpenPrice: response.signal.roundOpenPrice,
      action: prediction.action,
      probability: prediction.probability,
      contractEntryPrice: quotePrice,
      contractQuoteSource: quotePrice !== null ? 'live_ask' : 'unavailable',
      targetLine: response.quote.targetLine ?? null,
      settled: false,
      settledMs: null,
      closePrice: null,
      actual: null,
      won: null,
    };
    return { ...history, predictions: [...history.predictions, record] };
  }

  const existing = history.predictions[existingIndex];
  const latePrice = priceForAction(response.quote, existing.action);
  if (existing.contractEntryPrice !== null || latePrice === null) return history;

  const updated: ModelPredictionRecord = {
    ...existing,
    contractEntryPrice: latePrice,
    contractQuoteSource: 'live_ask',
  };
  const predictions = [...history.predictions];
  predictions[existingIndex] = updated;
  return { ...history, predictions };
}

/** Same provisional proxy-settlement source and rule as V1's settlePreviousRound. */
export function settleModelRound(
  history: ModelHistory,
  response: Btc15PredictorResponse,
): ModelHistory {
  const previous = response.previousRound;
  if (!previous || previous.closePrice === null) return history;

  const index = history.predictions.findIndex((p) => p.roundId === previous.roundId);
  if (index === -1 || history.predictions[index].settled) return history;

  const record = history.predictions[index];
  const actual = proxyActual(previous.closePrice, record);
  const updated: ModelPredictionRecord = {
    ...record,
    settled: true,
    settledMs: Date.now(),
    closePrice: previous.closePrice,
    actual,
    won: record.action === actual,
    settlementSource: 'coinbase_proxy',
  };
  const predictions = [...history.predictions];
  predictions[index] = updated;
  return { ...history, predictions };
}

/** Same official re-grade as V1's applyOfficialResults. */
export function applyOfficialModelResults(
  history: ModelHistory,
  results: Map<number, OfficialRoundResult>,
  nowMs: number = Date.now(),
): ModelHistory {
  const predictions = applyOfficialResultsTo(history.predictions, results, nowMs);
  return predictions === history.predictions ? history : { ...history, predictions };
}

export interface ModelRecordStats {
  wins: number;
  losses: number;
  total: number;
  rate: number | null;
}

function settledRecords(
  history: ModelHistory,
): Array<ModelPredictionRecord & { won: boolean }> {
  return history.predictions
    .filter(
      (p): p is ModelPredictionRecord & { won: boolean } => p.settled && p.won !== null,
    )
    .sort((a, b) => a.createdMs - b.createdMs);
}

function statsFor(records: Array<{ won: boolean }>, n: number): ModelRecordStats {
  const xs = records.slice(-n);
  if (!xs.length) return { wins: 0, losses: 0, total: 0, rate: null };
  const wins = xs.filter((x) => x.won).length;
  return { wins, losses: xs.length - wins, total: xs.length, rate: wins / xs.length };
}

export interface ModelAccuracyWindows {
  overall: ModelRecordStats;
  windows: Array<{ n: number; stats: ModelRecordStats }>;
}

const ACCURACY_WINDOW_SIZES = [10, 100, 250, 500, 1200];

export function computeModelAccuracyWindows(history: ModelHistory): ModelAccuracyWindows {
  const resolved = settledRecords(history);
  return {
    overall: statsFor(resolved, resolved.length),
    windows: ACCURACY_WINDOW_SIZES.map((n) => ({ n, stats: statsFor(resolved, n) })),
  };
}

export interface ModelStrategyPnlResult {
  eligibleSignals: number;
  pricedSignals: number;
  trades: number;
  valueSkips: number;
  wins: number;
  losses: number;
  stake: number;
  /** Net of Kalshi's taker fee on every buy. */
  pnl: number;
  /** Kalshi taker fees paid across every funded trade (already subtracted from pnl). */
  feesPaid: number;

  roi: number | null;
  poolReturn: number | null;
  startingPool: number;
  endingPool: number;
}

/**
 * Replays quarter-Kelly sizing chronologically over settled predictions
 * with a real quoted entry price — the same replay V1's computeStrategyPnl
 * does, minus the SKIP filter (there is no SKIP concept here; every settled,
 * priced round is a candidate, and calculateAllocation's own minimum-edge
 * gate is what actually leaves a round unfunded). Kalshi's taker fee is
 * charged on every buy.
 */
export function computeModelStrategyPnl(
  history: ModelHistory,
  startingPool: number = DEFAULT_CONFIG.simulatedStartingPool,
  fees: FeeModel = KALSHI_TAKER_FEES,
): ModelStrategyPnlResult {
  const ordered = history.predictions
    .filter((p) => p.settled)
    .sort((a, b) => a.createdMs - b.createdMs);

  let bankroll = startingPool;
  let stake = 0;
  let pnl = 0;
  let feesPaid = 0;
  let trades = 0;
  let wins = 0;
  let pricedSignals = 0;

  for (const prediction of ordered) {
    const entry = prediction.contractEntryPrice;
    if (entry === null || !validContractPrice(entry)) continue;
    pricedSignals++;

    const allocation = calculateAllocation(
      prediction.action,
      prediction.probability,
      entry,
      bankroll,
    );
    if (allocation.stake <= 0) continue;

    const tradePnl = heldTradePnl(allocation.stake, entry, Boolean(prediction.won), fees);

    trades++;
    stake += allocation.stake;
    feesPaid += tradeFees(allocation.stake, entry, null, fees);
    pnl += tradePnl;
    bankroll += tradePnl;
    if (prediction.won) wins++;
  }

  return {
    eligibleSignals: ordered.length,
    pricedSignals,
    trades,
    valueSkips: pricedSignals - trades,
    wins,
    losses: trades - wins,
    stake,
    pnl,
    feesPaid,
    roi: stake ? pnl / stake : null,
    poolReturn: startingPool ? (bankroll - startingPool) / startingPool : null,
    startingPool,
    endingPool: bankroll,
  };
}

const modelPredictionRecordSchema = z.object({
  roundId: z.string(),
  createdMs: z.number(),
  roundCloseMs: z.number(),
  roundOpenPrice: z.number(),
  action: z.enum(['UP', 'DOWN']),
  probability: z.number(),
  contractEntryPrice: z.number().nullable(),
  contractQuoteSource: z.enum(['live_ask', 'unavailable']),
  settled: z.boolean(),
  settledMs: z.number().nullable(),
  closePrice: z.number().nullable(),
  actual: z.enum(['UP', 'DOWN']).nullable(),
  won: z.boolean().nullable(),
  targetLine: z.number().nullable().optional(),
  settlementSource: z.enum(['kalshi_official', 'coinbase_proxy']).optional(),
});

const modelHistorySchema = z.object({
  schemaVersion: z.number(),
  predictions: z.array(modelPredictionRecordSchema),
});

export interface ParseModelHistoryResult {
  ok: boolean;
  history: ModelHistory | null;
  error: string | null;
}

export function parseModelHistory(raw: string): ParseModelHistoryResult {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, history: null, error: 'That data is not valid JSON.' };
  }

  const parsed = modelHistorySchema.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      history: null,
      error:
        parsed.error.issues[0]?.message ??
        'That data does not match the expected schema.',
    };
  }

  return { ok: true, history: parsed.data, error: null };
}

export interface ModelMergeResult {
  merged: ModelHistory;
  addedCount: number;
  duplicateCount: number;
  updatedCount: number;
}

/** Same merge rule as V1's mergeHistory: dedupe by round id, never replace a settled record with an unsettled one, and prefer an official settlement over a proxy one. */
export function mergeModelHistory(
  existing: ModelHistory,
  incoming: ModelHistory,
): ModelMergeResult {
  const byRoundId = new Map(existing.predictions.map((p) => [p.roundId, p]));
  let addedCount = 0;
  let duplicateCount = 0;
  let updatedCount = 0;

  for (const incomingRecord of incoming.predictions) {
    const current = byRoundId.get(incomingRecord.roundId);
    if (!current) {
      byRoundId.set(incomingRecord.roundId, incomingRecord);
      addedCount++;
    } else if (incomingSupersedes(current, incomingRecord)) {
      byRoundId.set(incomingRecord.roundId, incomingRecord);
      updatedCount++;
    } else {
      duplicateCount++;
    }
  }

  return {
    merged: {
      schemaVersion: MODEL_HISTORY_SCHEMA_VERSION,
      predictions: Array.from(byRoundId.values()),
    },
    addedCount,
    duplicateCount,
    updatedCount,
  };
}
